import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/api';
import { isAdminOrOwner, isManagerOrAbove } from '../../lib/roles';
import { subscribeToPurchasesUpdates, subscribeToSuppliersUpdates, subscribeToCategoriesUpdates } from '../../services/socket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { Plus, Edit, Trash2, X, Package, Eye, Pencil, Search, Download, Loader2 } from 'lucide-react';
import { exportPurchasesToExcel } from '../../utils/excelExport';
import { formatProductUnitType } from '../../utils/unitLabels';
import { formatDate, formatDateTime, formatCurrency, toCalendarDateString, todayCalendarDateString } from '../../utils/format';
import { normalizeForSearch } from '../../utils/searchUtils';
import { getTokens } from '../../utils/tokens';
import { getAdminSelectCSS, getAdminFormSelectCSS } from '../../utils/adminSelect';
import {
  PageHeader,
  StatStrip,
  PrimaryBtn,
  GhostBtn,
  Toolbar,
  FilterChip,
  StatusPill,
  DataTable,
  Pagination,
  MonoMoney,
  AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';
import {
  normalizePurchaseQuantityInput,
  purchaseQuantityValidForUnit,
  formatPurchaseStockPreviewDisplay,
} from '../../utils/purchaseQuantity';

const Purchases = () => {
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);

  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [deletingSupplier, setDeletingSupplier] = useState(null);
  const [viewingPurchase, setViewingPurchase] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [deletingPurchase, setDeletingPurchase] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    supplier_id: 'all',
    category_id: 'all',
    start_date: '',
    end_date: '',
    search: '',
  });

  const canManage = isManagerOrAbove(user?.role);
  const canEditDelete = isAdminOrOwner(user?.role);

// ── Style helpers ────────────────────────────────────────────────────────────
const filterSelect = getAdminSelectCSS(tk, 'purch-filter');
const formSelect = getAdminFormSelectCSS(tk, 'purch-form');

const dateInput = {
    height: 34, padding: '0 10px', background: tk.surface,
    border: `1px solid ${tk.border}`, borderRadius: 8,
    color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none',
  };

  const inputStyle = {
    width: '100%', height: 40, background: tk.sunken,
    border: `1px solid ${tk.border}`, borderRadius: 8,
    padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    fontSize: 13, outline: 'none',
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.4,
    marginBottom: 6, display: 'block',
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };

  const modalBox = {
    background: tk.surface, border: `1px solid ${tk.border}`,
    borderRadius: 14, padding: 0, maxWidth: 460,
    width: '100%', margin: '0 16px', maxHeight: '90vh',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    boxShadow: tk.shadowLg,
  };

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => { loadSuppliers(); }, []);

  const loadCategories = async () => {
    try {
      const res = await apiService.getCategories();
      setCategories(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  useEffect(() => { loadPurchases(); }, [filters]);

  useEffect(() => {
    const unsubscribe = subscribeToPurchasesUpdates(loadPurchases);
    return unsubscribe;
  }, [filters]);

  useEffect(() => {
    const unsubscribe = subscribeToSuppliersUpdates(loadSuppliers);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToCategoriesUpdates(loadCategories);
    return unsubscribe;
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await apiService.getSuppliers();
      setSuppliers(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load suppliers', err);
    }
  };

  const loadPurchases = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        search: filters.search?.trim() || undefined,
      };
      if (filters.supplier_id && filters.supplier_id !== 'all') {
        params.supplier_id = filters.supplier_id;
      }
      if (filters.category_id && filters.category_id !== 'all') {
        params.category_id = filters.category_id;
      }
      const res = await apiService.getPurchases(params);
      setPurchases(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('alerts.failedToLoadPurchases'));
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil((purchases || []).length / itemsPerPage);
  const currentPurchases = React.useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return (purchases || []).slice(indexOfFirstItem, indexOfLastItem);
  }, [purchases, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [filters]);

  // ── Derived stats ─────────────────────────────────────────────────────────────
  const totalAmount = purchases.reduce((sum, p) => sum + parseFloat(p.total_price || 0), 0);

  const todayPurchasesCount = React.useMemo(() => {
    const today = new Date().toDateString();
    return purchases.filter((p) => {
      if (!p.purchased_at) return false;
      return new Date(p.purchased_at).toDateString() === today;
    }).length;
  }, [purchases]);
  const todayPurchasesTotal = React.useMemo(() => {
    const today = new Date().toDateString();
    return purchases
      .filter((p) => {
        if (!p.purchased_at) return false;
        return new Date(p.purchased_at).toDateString() === today;
      })
      .reduce((sum, p) => sum + parseFloat(p.total_price || 0), 0);
  }, [purchases]);

  const hasActiveFilters =
    filters.supplier_id !== 'all' ||
    filters.category_id !== 'all' ||
    filters.search?.trim() !== '' ||
    !!filters.start_date ||
    !!filters.end_date;

  const clearFilters = () => {
    setFilters({
      supplier_id: 'all',
      category_id: 'all',
      start_date: '',
      end_date: '',
      search: '',
    });
  };

  // ── Filter options ─────────────────────────────────────────────────────────
  const supplierOptions = [
    { value: 'all', label: t('common.all') },
    ...suppliers.map((s) => ({ value: String(s.id), label: s.name })),
  ];

  const categoryOptions = [
    { value: 'all', label: t('common.all') },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  const categoryNameById = useMemo(
    () => Object.fromEntries((categories || []).map((c) => [String(c.id), c.name || ''])),
    [categories],
  );

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    { key: 'product_name', label: t('inventory.productName') },
    { key: 'supplier_name', label: t('purchases.selectSupplier') },
    { key: 'purchased_at', label: t('purchases.date') },
    { key: 'quantity', label: t('common.quantity') },
    { key: 'unit_price', label: t('purchases.costPrice') },
    { key: 'total_price', label: t('common.total') },
    { key: 'actions', label: t('common.actions') },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case 'product_name':
        return (
          <span style={{ fontWeight: 500, color: tk.text }}>
            {row.product_name || '—'}
            {row.product_sku && (
              <span style={{
                display: 'block', fontSize: 10.5,
                fontFamily: '"JetBrains Mono", monospace',
                color: tk.textMuted, marginTop: 1,
              }}>
                {row.product_sku}
              </span>
            )}
          </span>
        );
      case 'supplier_name':
        return (
          <span style={{ fontWeight: 500, color: tk.textSecondary }}>
            {row.supplier_name || '—'}
          </span>
        );
      case 'purchased_at':
        return (
          <span style={{ color: tk.textSecondary, fontSize: 12 }}>
            {row.purchased_at ? formatDate(row.purchased_at) : '—'}
          </span>
        );
      case 'quantity':
        return (
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 600, color: tk.text,
          }}>
            {row.quantity ?? '—'}
          </span>
        );
      case 'unit_price':
        return <MonoMoney amount={row.unit_price} tk={tk} size={12.5} weight={500} />;
      case 'total_price':
        return <MonoMoney amount={row.total_price} tk={tk} size={12.5} weight={600} />;
      case 'actions':
        return (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <button
              onClick={() => setViewingPurchase(row)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
              title={t('common.view')}
            >
              <Eye size={13} />
            </button>
            {canEditDelete && (
              <>
                <button
                  onClick={() => setEditingPurchase(row)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                    background: tk.accentSoft, border: 'none',
                    color: tk.accentText, cursor: 'pointer',
                  }}
                  title={t('common.edit')}
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => setDeletingPurchase(row)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                    background: tk.dangerSoft, border: 'none',
                    color: tk.dangerText, cursor: 'pointer',
                  }}
                  title={t('common.delete')}
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </span>
        );
      default:
        return null;
    }
  };

  // ── AddPurchaseModal ───────────────────────────────────────────────────────
  const AddPurchaseModal = ({ onClose, onSave }) => {
    const [searchInput, setSearchInput] = useState('');
    const [product, setProduct] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchError, setSearchError] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [formData, setFormData] = useState({
      supplier_id: '',
      unit_price: '',
      unit_selling_price: '',
      quantity: '',
      purchased_at: todayCalendarDateString(),
    });
    const [saving, setSaving] = useState(false);
    const searchRef = useRef(null);

    useEffect(() => { searchRef.current?.focus(); }, []);

    const handleSelectProduct = (prod) => {
      setProduct(prod);
      setSearchResults([]);
      setSearchError(null);
      setFormData((prev) => ({
        ...prev,
        unit_price: prod.cost_price ?? '',
        unit_selling_price: prod.selling_price ?? '',
        quantity: '',
      }));
    };

    const handleSearch = async (e) => {
      e?.preventDefault();
      const q = searchInput.trim();
      if (!q) return;
      setSearchLoading(true);
      setSearchError(null);
      setProduct(null);
      setSearchResults([]);
      try {
        try {
          const res = await apiService.getProductByBarcode(q);
          const prod = res?.data || res;
          handleSelectProduct(prod);
          return;
        } catch (_) {
          // not found by barcode, fall through to name search
        }
        const res = await apiService.searchProducts(q);
        const list = Array.isArray(res?.data) ? res.data : [];
        setSearchResults(list);
        if (list.length === 1) {
          handleSelectProduct(list[0]);
        } else if (list.length === 0) {
          setSearchError(t('purchases.productNotFound'));
        }
      } catch (err) {
        setSearchError(t('purchases.productNotFound'));
      } finally {
        setSearchLoading(false);
      }
    };

    const handleSubmit = async (e) => {
      e?.preventDefault();
      if (!product) return;
      setSaving(true);
      try {
        const qty = parseFloat(formData.quantity);
        const cost = parseFloat(formData.unit_price);
        const selling = parseFloat(formData.unit_selling_price);
        if (!Number.isFinite(qty) || qty <= 0) throw new Error('Quantity must be greater than 0');
        if (!purchaseQuantityValidForUnit(qty, product.unit_type === 'kg' ? 'kg' : 'piece')) {
          showToast(
            product.unit_type === 'kg'
              ? t('alerts.purchaseQuantityKgMaxDecimals')
              : t('alerts.purchaseQuantityPieceWholeNumber'),
            'warning',
          );
          return;
        }
        if (isNaN(cost) || cost <= 0) throw new Error(t('alerts.purchaseUnitPriceInvalid'));
        if (isNaN(selling) || selling <= 0) throw new Error('Selling price must be greater than 0');
        if (selling < cost) throw new Error(t('alerts.purchaseSellingBelowUnitCost'));
        if (!formData.supplier_id) throw new Error(t('purchases.selectSupplier'));

        await apiService.createPurchase({
          supplier_id: formData.supplier_id,
          product_id: product.id,
          quantity: qty,
          unit_price: cost,
          unit_selling_price: selling,
          purchased_at: formData.purchased_at,
        });
        onSave();
      } catch (err) {
        const raw = err.response?.data?.error || err.body?.error || err.message || '';
        const msg =
          raw === 'Unit selling price must be greater than or equal to unit price'
            ? t('alerts.purchaseSellingBelowUnitCost')
            : raw === 'Unit price (cost) must be greater than 0'
              || String(raw).includes('purchases_unit_price_check')
              ? t('alerts.purchaseUnitPriceInvalid')
            : raw === 'Quantity for piece products must be a whole number'
              ? t('alerts.purchaseQuantityPieceWholeNumber')
              : raw === 'Kg quantity may have at most 3 decimal places'
                ? t('alerts.purchaseQuantityKgMaxDecimals')
              : raw || t('alerts.failedToSave').replace('{error}', t('alerts.unknownError'));
        showToast(msg, "error");
      } finally {
        setSaving(false);
      }
    };

    const currentStock = product ? parseFloat(product.stock_quantity || 0) : 0;
    const qtyNum = parseFloat(formData.quantity) || 0;
    const afterStock = currentStock + qtyNum;

    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('purchases.addPurchase')}
            </span>
            <button
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>

          <form
            id="purchase-add-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!product) handleSearch();
              else handleSubmit(e);
            }}
            style={{ padding: '20px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div>
              <label style={labelStyle}>{t('inventory.barcode')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  ref={searchRef}
                  type="text"
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); setSearchError(null); setSearchResults([]); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(e)}
                  placeholder={t('purchases.barcodePlaceholder')}
                  maxLength={50}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={searchLoading || !searchInput.trim()}
                  style={{
                    height: 40, padding: '0 14px', borderRadius: 8,
                    background: tk.accent, border: 'none',
                    color: '#fff', cursor: searchLoading || !searchInput.trim() ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                    opacity: searchLoading || !searchInput.trim() ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {searchLoading ? t('common.loading') : t('common.search')}
                </button>
              </div>
              {searchError && (
                <p style={{ fontSize: 12, marginTop: 6, color: tk.dangerText }}>{searchError}</p>
              )}
              {searchResults.length > 0 && !product && (
                <div style={{
                  marginTop: 8, maxHeight: 192, overflowY: 'auto',
                  border: `1px solid ${tk.border}`, borderRadius: 8,
                  background: tk.surface,
                }}>
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleSelectProduct(p)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '10px 14px', background: 'transparent', border: 'none',
                        borderBottom: `1px solid ${tk.border}`, cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{p.name}</span>
                      {p.sku && (
                        <span style={{ fontSize: 11.5, color: tk.textMuted, marginLeft: 8 }}>
                          {p.sku}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {product && (
              <>
                {suppliers.length === 0 && (
                  <p style={{ fontSize: 12.5, color: tk.dangerText, background: tk.dangerSoft, padding: '8px 12px', borderRadius: 8 }}>
                    {t('purchases.noSuppliers')}
                  </p>
                )}

                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', borderRadius: 10,
                  border: `1px solid ${tk.border}`, background: tk.surfaceAlt,
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{product.name}</div>
                    {product.sku && (
                      <div style={{ fontSize: 11.5, fontFamily: '"JetBrains Mono", monospace', color: tk.textMuted, marginTop: 2 }}>
                        SKU: {product.sku}
                      </div>
                    )}
                    <div style={{ fontSize: 11.5, color: tk.textSecondary, marginTop: 2 }}>
                      {t('inventory.stockQuantity')}: {currentStock}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setProduct(null); setSearchInput(''); setSearchResults([]); }}
                    style={{
                      fontSize: 11.5, fontWeight: 600, height: 28, padding: '0 10px',
                      borderRadius: 6, background: tk.accentSoft, border: 'none',
                      color: tk.accentText, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t('purchases.changeProduct')}
                  </button>
                </div>

                <div>
                  <label style={labelStyle}>{t('purchases.selectSupplier')}</label>
        <select
                  value={formData.supplier_id}
                  onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                  className={formSelect.className}
                  required
                >
                  <option value="">{t('purchases.selectSupplier')}</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                </div>

                <div>
                  <label style={labelStyle}>{t('purchases.costPrice')} ({t('currency.egp')})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="999999.99"
                    value={formData.unit_price}
                    onChange={(e) => {
                      if (e.target.value !== '' && parseFloat(e.target.value) > 999999.99) return;
                      setFormData({ ...formData, unit_price: e.target.value });
                    }}
                    style={inputStyle}
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>{t('purchases.sellingPrice')} ({t('currency.egp')})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="999999.99"
                    value={formData.unit_selling_price}
                    onChange={(e) => {
                      if (e.target.value !== '' && parseFloat(e.target.value) > 999999.99) return;
                      setFormData({ ...formData, unit_selling_price: e.target.value });
                    }}
                    style={inputStyle}
                    required
                  />
                  {(formData.unit_price !== '' || formData.unit_selling_price !== '') && (
                    <p style={{
                      fontSize: 11.5, marginTop: 5, fontWeight: 600,
                      color: (parseFloat(formData.unit_selling_price) || 0) - (parseFloat(formData.unit_price) || 0) >= 0
                        ? tk.successText : tk.dangerText,
                    }}>
                      {t('inventory.profitMargin')}:{' '}
                      {formatCurrency((parseFloat(formData.unit_selling_price) || 0) - (parseFloat(formData.unit_price) || 0))}{' '}
                      {t('currency.egp')}
                    </p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>{t('purchases.quantity')}</label>
                  <input
                    type="number"
                    step={product.unit_type === 'kg' ? '0.001' : '1'}
                    min={product.unit_type === 'kg' ? '0.001' : '1'}
                    max="9999999.99"
                    value={formData.quantity}
                    onChange={(e) => {
                      const next = normalizePurchaseQuantityInput(e.target.value, product.unit_type);
                      if (next !== '' && parseFloat(next) > 9999999.99) return;
                      setFormData({ ...formData, quantity: next });
                    }}
                    style={inputStyle}
                    required
                  />
                  <p style={{ fontSize: 10.5, color: tk.textMuted, marginTop: 4 }}>
                    {product.unit_type === 'kg' ? t('purchases.quantityHintKg') : t('purchases.quantityHintPiece')}
                  </p>
                  {formData.quantity && (
                    <p style={{ fontSize: 11.5, marginTop: 5, color: tk.textSecondary }}>
                      {t('purchases.inventoryBeforeAfter')
                        .replace('{before}', formatPurchaseStockPreviewDisplay(currentStock))
                        .replace('{quantity}', formatPurchaseStockPreviewDisplay(qtyNum))
                        .replace('{after}', formatPurchaseStockPreviewDisplay(afterStock))}
                    </p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>{t('purchases.date')}</label>
                  <input
                    type="date"
                    value={formData.purchased_at}
                    onChange={(e) => setFormData({ ...formData, purchased_at: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </>
            )}
          </form>

          {product && (
            <div style={{
              display: 'flex', gap: 10, padding: '14px 20px',
              borderTop: `1px solid ${tk.border}`, background: tk.surface,
            }}>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                form="purchase-add-form"
                disabled={saving}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.accent, border: 'none',
                  color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? t('common.loading') : t('purchases.addPurchase')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── EditPurchaseModal ──────────────────────────────────────────────────────
  const EditPurchaseModal = ({ purchase, suppliers: supplierList, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      supplier_id: purchase.supplier_id || '',
      unit_price: purchase.unit_price ?? '',
      unit_selling_price: purchase.unit_selling_price ?? '',
      quantity: purchase.quantity ?? '',
      purchased_at: toCalendarDateString(purchase.purchased_at) || todayCalendarDateString(),
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e?.preventDefault();
      setSaving(true);
      try {
        const qty = parseFloat(formData.quantity);
        const cost = parseFloat(formData.unit_price);
        const selling = parseFloat(formData.unit_selling_price);
        const unitType = purchase.product_unit_type || 'piece';
        if (!Number.isFinite(qty) || qty <= 0) throw new Error('Quantity must be greater than 0');
        if (!purchaseQuantityValidForUnit(qty, unitType === 'kg' ? 'kg' : 'piece')) {
          showToast(
            unitType === 'kg'
              ? t('alerts.purchaseQuantityKgMaxDecimals')
              : t('alerts.purchaseQuantityPieceWholeNumber'),
            'warning',
          );
          return;
        }
        if (isNaN(cost) || cost <= 0) throw new Error(t('alerts.purchaseUnitPriceInvalid'));
        if (isNaN(selling) || selling <= 0) throw new Error('Selling price must be greater than 0');
        if (selling < cost) throw new Error(t('alerts.purchaseSellingBelowUnitCost'));
        if (!formData.supplier_id) throw new Error(t('purchases.selectSupplier'));

        await apiService.updatePurchase(purchase.id, {
          supplier_id: formData.supplier_id,
          quantity: qty,
          unit_price: cost,
          unit_selling_price: selling,
          purchased_at: formData.purchased_at,
        });
        onSave();
      } catch (err) {
        const raw = err.response?.data?.error || err.body?.error || err.message || '';
        const msg =
          raw === 'Unit selling price must be greater than or equal to unit price'
            ? t('alerts.purchaseSellingBelowUnitCost')
            : raw === 'Unit price (cost) must be greater than 0'
              || String(raw).includes('purchases_unit_price_check')
              ? t('alerts.purchaseUnitPriceInvalid')
            : raw === 'Quantity for piece products must be a whole number'
              ? t('alerts.purchaseQuantityPieceWholeNumber')
              : raw === 'Kg quantity may have at most 3 decimal places'
                ? t('alerts.purchaseQuantityKgMaxDecimals')
              : raw || t('alerts.failedToSave').replace('{error}', t('alerts.unknownError'));
        showToast(msg, "error");
      } finally {
        setSaving(false);
      }
    };

    const profitMargin = (parseFloat(formData.unit_selling_price) || 0) - (parseFloat(formData.unit_price) || 0);
    const editUnitType = purchase.product_unit_type || 'piece';

    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('purchases.editPurchase')}
            </span>
            <button
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>

          <form
            id="purchase-edit-form"
            onSubmit={handleSubmit}
            style={{ padding: '20px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div style={{
              padding: '12px 14px', borderRadius: 10,
              border: `1px solid ${tk.border}`, background: tk.surfaceAlt,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>
                {purchase.product_name || '—'}
              </div>
              {purchase.product_sku && (
                <div style={{ fontSize: 11.5, fontFamily: '"JetBrains Mono", monospace', color: tk.textMuted, marginTop: 2 }}>
                  SKU: {purchase.product_sku}
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle}>{t('purchases.selectSupplier')}</label>
          <select
                  value={formData.supplier_id}
                  onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                  className={formSelect.className}
                  required
                >
                  <option value="">{t('purchases.selectSupplier')}</option>
                  {supplierList.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
            </div>

            <div>
              <label style={labelStyle}>{t('purchases.costPrice')} ({t('currency.egp')})</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelStyle}>{t('purchases.sellingPrice')} ({t('currency.egp')})</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.unit_selling_price}
                onChange={(e) => setFormData({ ...formData, unit_selling_price: e.target.value })}
                style={inputStyle}
                required
              />
              <p style={{
                fontSize: 11.5, marginTop: 5, fontWeight: 600,
                color: profitMargin >= 0 ? tk.successText : tk.dangerText,
              }}>
                {t('inventory.profitMargin')}: {formatCurrency(profitMargin)} {t('currency.egp')}
              </p>
            </div>

            <div>
              <label style={labelStyle}>{t('purchases.quantity')}</label>
              <input
                type="number"
                step={editUnitType === 'kg' ? '0.001' : '1'}
                min={editUnitType === 'kg' ? '0.001' : '1'}
                value={formData.quantity}
                onChange={(e) => {
                  const next = normalizePurchaseQuantityInput(e.target.value, editUnitType);
                  setFormData({ ...formData, quantity: next });
                }}
                style={inputStyle}
                required
              />
              <p style={{ fontSize: 10.5, color: tk.textMuted, marginTop: 4 }}>
                {editUnitType === 'kg' ? t('purchases.quantityHintKg') : t('purchases.quantityHintPiece')}
              </p>
            </div>

            <div>
              <label style={labelStyle}>{t('purchases.date')}</label>
              <input
                type="date"
                value={formData.purchased_at}
                onChange={(e) => setFormData({ ...formData, purchased_at: e.target.value })}
                style={inputStyle}
              />
            </div>
          </form>

          <div style={{
            display: 'flex', gap: 10, padding: '14px 20px',
            borderTop: `1px solid ${tk.border}`, background: tk.surface,
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                flex: 1, height: 38, borderRadius: 8,
                background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="purchase-edit-form"
              disabled={saving}
              style={{
                flex: 1, height: 38, borderRadius: 8,
                background: tk.accent, border: 'none',
                color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? t('common.loading') : t('common.update')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── SupplierManagementModal ────────────────────────────────────────────────
  const SupplierManagementModal = ({ onClose }) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingSupplierLocal, setEditingSupplierLocal] = useState(null);
    const [formData, setFormData] = useState({ name: '', phone: '' });
    const [saving, setSaving] = useState(false);
    const [supplierListSearch, setSupplierListSearch] = useState('');

    const filteredSuppliers = useMemo(() => {
      const raw = supplierListSearch.trim();
      if (!raw) return suppliers;
      const qNorm = normalizeForSearch(raw);
      const qDigits = raw.replace(/\D/g, '');
      return suppliers.filter((s) => {
        const name = s.name || '';
        const phone = s.phone != null ? String(s.phone) : '';
        if (qNorm && normalizeForSearch(name).includes(qNorm)) return true;
        if (phone && (phone.includes(raw) || (qDigits.length > 0 && phone.replace(/\D/g, '').includes(qDigits)))) return true;
        return false;
      });
    }, [suppliers, supplierListSearch]);

    const handleSaveSupplier = async (e) => {
      e.preventDefault();
      if (!formData.name?.trim()) return;
      if (formData.name.trim().length > 30) {
        showToast(t('alerts.supplierNameMaxLength'), "warning");
        return;
      }
      if (formData.phone?.trim() && formData.phone.trim().length > 15) {
        showToast(t('alerts.supplierPhoneMaxLength'), "warning");
        return;
      }
      const phoneTrimmed = formData.phone?.trim() || '';
      if (phoneTrimmed) {
        const duplicate = suppliers.find((s) => s.phone === phoneTrimmed && s.id !== editingSupplierLocal?.id);
        if (duplicate) {
          showToast(t('purchases.supplierPhoneUnique'), "warning");
          return;
        }
      }
      const nameNorm = normalizeForSearch(formData.name);
      if (nameNorm) {
        const nameDup = suppliers.find(
          (s) => s.id !== editingSupplierLocal?.id && normalizeForSearch(s.name || '') === nameNorm
        );
        if (nameDup) {
          showToast(t('purchases.supplierNameUnique'), "warning");
          return;
        }
      }
      setSaving(true);
      try {
        if (editingSupplierLocal) {
          await apiService.updateSupplier(editingSupplierLocal.id, {
            name: formData.name.trim(),
            phone: formData.phone?.trim() || null,
          });
        } else {
          await apiService.createSupplier({
            name: formData.name.trim(),
            phone: formData.phone?.trim() || null,
          });
        }
        await loadSuppliers();
        setShowAddForm(false);
        setEditingSupplierLocal(null);
        setFormData({ name: '', phone: '' });
      } catch (err) {
        const raw = err.response?.data?.error || err.body?.error || err.message || '';
        const nameTaken = 'A supplier with this name already exists';
        const phoneTaken = 'This number already exists';
        let showError = raw || t('alerts.failedToSaveSupplier');
        if (raw === nameTaken) showError = t('purchases.supplierNameUnique');
        else if (raw === phoneTaken) showError = t('purchases.supplierPhoneUnique');
        showToast(showError, "error");
      }
      setSaving(false);
    };

    return (
      <div style={overlay}>
        <div style={{
          background: tk.surface, border: `1px solid ${tk.border}`,
          borderRadius: 14, width: '100%', maxWidth: 480,
          margin: '0 16px', display: 'flex', flexDirection: 'column',
          maxHeight: '80vh', overflow: 'hidden', boxShadow: tk.shadowLg,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('purchases.manageSuppliers')}
            </span>
            <button
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
            {(showAddForm || editingSupplierLocal) && (
              <form onSubmit={handleSaveSupplier} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${tk.border}` }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
                  {editingSupplierLocal ? t('purchases.editSupplier') : t('purchases.addSupplier')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('purchases.supplierName')}
                    maxLength={30}
                    style={{ ...inputStyle, height: 38 }}
                    autoFocus
                  />
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                    placeholder={t('purchases.supplierPhone')}
                    maxLength={15}
                    style={{ ...inputStyle, height: 38 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      height: 34, padding: '0 16px', borderRadius: 8,
                      background: tk.accent, border: 'none',
                      color: '#fff', cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                    }}
                  >
                    {saving ? t('common.loading') : t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingSupplierLocal(null);
                      setFormData({ name: '', phone: '' });
                    }}
                    style={{
                      height: 34, padding: '0 12px', borderRadius: 8,
                      background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                      color: tk.textSecondary, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5,
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            )}

            {!showAddForm && !editingSupplierLocal && (
              <button
                onClick={() => { setShowAddForm(true); setFormData({ name: '', phone: '' }); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 34, padding: '0 14px', borderRadius: 8, marginBottom: 12,
                  background: tk.accentSoft, border: 'none',
                  color: tk.accentText, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                }}
              >
                <Plus size={13} />
                {t('purchases.addSupplier')}
              </button>
            )}

            {suppliers.length > 0 && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 12,
                  padding: '0 12px',
                  height: 38,
                  background: tk.sunken,
                  border: `1px solid ${tk.border}`,
                  borderRadius: 8,
                }}
              >
                <Search size={15} color={tk.textMuted} style={{ flexShrink: 0 }} />
                <input
                  type="search"
                  value={supplierListSearch}
                  onChange={(e) => setSupplierListSearch(e.target.value)}
                  placeholder={t('purchases.searchSuppliersPlaceholder')}
                  autoComplete="off"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    border: 'none',
                    background: 'transparent',
                    color: tk.text,
                    fontFamily: 'inherit',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {suppliers.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 13 }}>
                  {t('purchases.noSuppliers')}
                </p>
              ) : filteredSuppliers.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 13 }}>
                  {t('purchases.suppliersNoSearchMatches')}
                </p>
              ) : (
                filteredSuppliers.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      border: `1px solid ${tk.border}`, background: tk.surfaceAlt,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{s.name}</div>
                      {s.phone && (
                        <div style={{ fontSize: 11.5, fontFamily: '"JetBrains Mono", monospace', color: tk.textMuted, marginTop: 2 }}>
                          {s.phone}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => {
                          setEditingSupplierLocal(s);
                          setFormData({ name: s.name, phone: s.phone || '' });
                        }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                          background: tk.accentSoft, border: 'none',
                          color: tk.accentText, cursor: 'pointer',
                        }}
                      >
                        <Edit size={13} />
                      </button>
                      <button
                        onClick={() => setDeletingSupplier(s)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                          background: tk.dangerSoft, border: 'none',
                          color: tk.dangerText, cursor: 'pointer',
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── DetailRow helper for view modal ───────────────────────────────────────────
  const DetailRow = ({ label, value, highlight }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 10, borderBottom: `1px solid ${tk.border}` }}>
      <span style={{ fontSize: 12, color: tk.textMuted }}>{label}</span>
      <span style={{
        fontSize: 12.5, fontWeight: highlight ? 600 : 400,
        color: highlight ? tk.text : tk.textSecondary,
        textAlign: 'right', maxWidth: '55%',
      }}>
        {value}
      </span>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20,
    minHeight: '100%', fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };
  const toolbarFilterLabelStyle = {
    fontSize: 10.5,
    color: tk.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  return (
    <div style={wrapperStyle} className={`theme-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        tk={tk}
        title={t('purchases.title')}
        subtitle={t('purchases.subtitle') || 'Purchase orders from suppliers'}
        actions={
          <>
            <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={async () => {
              if (!purchases.length) return;
              setExporting(true);
              try {
                const labels = t('purchases.excelExport');
                const L = typeof labels === 'object' && labels !== null ? labels : {};
                const today = todayCalendarDateString();
                const rowsForExport = purchases.map((p) => {
                  const fromJoin = (p.category_name && String(p.category_name).trim()) || '';
                  const fromId =
                    p.category_id != null ? categoryNameById[String(p.category_id)] || '' : '';
                  return {
                    ...p,
                    category_name: fromJoin || fromId,
                  };
                });
                await exportPurchasesToExcel(rowsForExport, `purchases-${today}.xlsx`, {
                  ...L,
                  formatUnitType: (u) => formatProductUnitType(u, t),
                  categoryColorByName: Object.fromEntries(
                    categories
                      .filter((c) => c && String(c.name || '').trim() && c.color)
                      .map((c) => [String(c.name).trim(), String(c.color).trim()])
                  ),
                });
              } catch (err) {
                console.error('Export failed', err);
              } finally {
                setExporting(false);
              }
            }}>
              {exporting ? t('monthlyClosing.downloading') : t('purchases.downloadExcel')}
            </GhostBtn>
            {canManage && (
              <GhostBtn tk={tk} onClick={() => setShowSupplierModal(true)} icon={Package}>
                {t('purchases.manageSuppliers')}
              </GhostBtn>
            )}
            {canManage && (
              <PrimaryBtn tk={tk} onClick={() => setShowAddModal(true)} icon={Plus}>
                {t('purchases.addPurchase')}
              </PrimaryBtn>
            )}
          </>
        }
      />

      {loading ? (
        <StatStripSkeleton tk={tk} count={4} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: t('purchases.todayPurchases') || 'Today', value: String(todayPurchasesCount) },
            { label: t('purchases.todayTotal') || 'Today total', value: todayPurchasesTotal, money: true },
            { label: t('purchases.totalPurchases') || 'Total spent', value: totalAmount, money: true },
            { label: t('purchases.purchaseCount') || 'Entries', value: String(purchases.length) },
          ]}
        />
      )}

      {error && (
        <div style={{
          background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          color: tk.dangerText, fontSize: 12.5,
        }}>
          {t('common.error')}: {error}
        </div>
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('purchases.searchPlaceholder') || 'Search purchases...'}
        searchValue={filters.search}
        onSearchChange={(v) => setFilters((prev) => ({ ...prev, search: v }))}
        filters={
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={toolbarFilterLabelStyle}>{t('common.from')}</span>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
                style={dateInput}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={toolbarFilterLabelStyle}>{t('common.to')}</span>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
                style={dateInput}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
              <span style={toolbarFilterLabelStyle}>{t('inventory.category')}</span>
              <select
                value={filters.category_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, category_id: e.target.value }))}
                onFocus={categories.length === 0 ? loadCategories : undefined}
                style={{ minWidth: 160 }}
                className={filterSelect.className(filters.category_id !== 'all')}
                data-active={filterSelect.dataActive(filters.category_id !== 'all')}
              >
                {categoryOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 170 }}>
              <span style={toolbarFilterLabelStyle}>{t('purchases.supplier')}</span>
                <select
                  value={filters.supplier_id}
                  onChange={(e) => setFilters((prev) => ({ ...prev, supplier_id: e.target.value }))}
                  style={{ minWidth: 170 }}
                  className={filterSelect.className(filters.supplier_id !== 'all')}
                  data-active={filterSelect.dataActive(filters.supplier_id !== 'all')}
                >
                  {supplierOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  alignSelf: 'flex-end',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  height: 34, padding: '0 10px', borderRadius: 8,
                  background: tk.dangerSoft, border: 'none',
                  color: tk.dangerText, fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <X size={12} />
                {t('common.clear')}
              </button>
            )}
          </>
        }
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[0.7, 2.5, 1.5, 1.5, 1, 1.5, 1.5, 1]} />
      ) : (
        <DataTable
          tk={tk}
          columns={columns}
          rows={currentPurchases}
          renderCell={renderCell}
          emptyMessage={
            filters.search || filters.supplier_id !== 'all' || filters.category_id !== 'all'
              ? t('purchases.tryDifferentFilters')
              : t('purchases.addFirstPurchase')
          }
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Modals */}
      {showAddModal && (
        <AddPurchaseModal
          onClose={() => setShowAddModal(false)}
          onSave={() => { setShowAddModal(false); loadPurchases(); }}
        />
      )}
      {editingPurchase && (
        <EditPurchaseModal
          purchase={editingPurchase}
          suppliers={suppliers}
          onClose={() => setEditingPurchase(null)}
          onSave={() => { setEditingPurchase(null); loadPurchases(); }}
        />
      )}
      {showSupplierModal && (
        <SupplierManagementModal onClose={() => setShowSupplierModal(false)} />
      )}

      {/* View purchase details modal */}
      {viewingPurchase && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
                {t('purchases.purchaseDetails')}
              </span>
              <button
                onClick={() => setViewingPurchase(null)}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                  background: 'transparent', border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                }}
              >
                <X size={14} />
              </button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: tk.textMuted, marginBottom: 12 }}>
                  {t('purchases.productData')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: tk.surfaceAlt, borderRadius: 10, padding: '14px' }}>
                  <DetailRow label={t('inventory.productName')} value={viewingPurchase.product_name || '—'} highlight />
                  <DetailRow label={t('inventory.sku')} value={viewingPurchase.product_sku || '—'} />
                  <DetailRow label={t('common.quantity')} value={viewingPurchase.quantity ?? '—'} highlight />
                  {viewingPurchase.old_inventory != null && (
                    <DetailRow
                      label={t('purchases.oldInventory')}
                      value={parseFloat(viewingPurchase.old_inventory).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                    />
                  )}
                  {viewingPurchase.old_inventory != null && (
                    <DetailRow
                      label={t('purchases.newInventory')}
                      value={`${(parseFloat(viewingPurchase.old_inventory) || 0) + (parseFloat(viewingPurchase.quantity) || 0)} (${parseFloat(viewingPurchase.old_inventory || 0).toLocaleString()} + ${parseFloat(viewingPurchase.quantity || 0).toLocaleString()})`}
                      highlight
                    />
                  )}
                  {viewingPurchase.old_cost_price != null && (
                    <DetailRow
                      label={t('purchases.oldCostPrice')}
                      value={`${formatCurrency(viewingPurchase.old_cost_price)} ${t('currency.egp')}`}
                    />
                  )}
                  {viewingPurchase.old_selling_price != null && (
                    <DetailRow
                      label={t('purchases.oldSellingPrice')}
                      value={`${formatCurrency(viewingPurchase.old_selling_price)} ${t('currency.egp')}`}
                    />
                  )}
                  <DetailRow label={t('purchases.costPrice')} value={`${formatCurrency(viewingPurchase.unit_price)} ${t('currency.egp')}`} />
                  <DetailRow label={t('purchases.sellingPrice')} value={`${formatCurrency(viewingPurchase.unit_selling_price)} ${t('currency.egp')}`} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${tk.border}` }}>
                    <span style={{ fontSize: 12, color: tk.textMuted }}>{t('inventory.profitMargin')}</span>
                    <span style={{
                      fontSize: 12.5, fontWeight: 600,
                      color: (parseFloat(viewingPurchase.unit_selling_price || 0) - parseFloat(viewingPurchase.unit_price || 0)) >= 0
                        ? tk.successText : tk.dangerText,
                    }}>
                      {formatCurrency((parseFloat(viewingPurchase.unit_selling_price || 0) - parseFloat(viewingPurchase.unit_price || 0)))} {t('currency.egp')}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: tk.textMuted, marginBottom: 12 }}>
                  {t('purchases.supplierData')}
                </div>
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: '14px' }}>
                  <DetailRow label={t('purchases.selectSupplier')} value={viewingPurchase.supplier_name || '—'} highlight />
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: tk.textMuted, marginBottom: 12 }}>
                  {t('purchases.purchaseSummary')}
                </div>
                <div style={{ background: tk.surfaceAlt, borderRadius: 10, padding: '14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <DetailRow
                    label={t('purchases.date')}
                    value={viewingPurchase.purchased_at ? formatDate(viewingPurchase.purchased_at) : '—'}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: tk.textMuted }}>{t('common.total')}</span>
                    <MonoMoney amount={viewingPurchase.total_price} tk={tk} size={14} weight={700} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete purchase confirm */}
      {deletingPurchase && (
        <div style={{ ...overlay, zIndex: 60 }}>
          <div style={{
            background: tk.surface, border: `1px solid ${tk.border}`,
            borderRadius: 14, width: '100%', maxWidth: 400,
            margin: '0 16px', padding: '24px', boxShadow: tk.shadowLg,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
              {t('purchases.deletePurchase')}
            </div>
            <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 22, lineHeight: 1.5 }}>
              {t('purchases.deletePurchaseConfirm')
                .replace('{product}', deletingPurchase.product_name || '')
                .replace('{quantity}', deletingPurchase.quantity || '')}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setDeletingPurchase(null)}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  try {
                    await apiService.deletePurchase(deletingPurchase.id);
                    loadPurchases();
                    setDeletingPurchase(null);
                  } catch (err) {
                    showToast(err.body?.error || err.message || t('alerts.failedToDeletePurchase'), "error");
                  }
                }}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.danger, border: 'none',
                  color: '#fff', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete supplier confirm */}
      {deletingSupplier && (
        <div style={{ ...overlay, zIndex: 60 }}>
          <div style={{
            background: tk.surface, border: `1px solid ${tk.border}`,
            borderRadius: 14, width: '100%', maxWidth: 400,
            margin: '0 16px', padding: '24px', boxShadow: tk.shadowLg,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
              {t('purchases.deleteSupplier')}
            </div>
            <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 22, lineHeight: 1.5 }}>
              {t('purchases.deleteSupplierConfirm').replace('{name}', deletingSupplier.name)}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setDeletingSupplier(null)}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                  color: tk.textSecondary, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  try {
                    await apiService.deleteSupplier(deletingSupplier.id);
                    await loadSuppliers();
                    loadPurchases();
                    setDeletingSupplier(null);
                  } catch (err) {
                    const errMsg = err.body?.error || err.message || '';
                    const isSupplierHasPurchases = errMsg === 'Cannot delete supplier with existing purchases';
                    showToast(isSupplierHasPurchases
                      ? t('purchases.supplierHasPurchases')
                      : errMsg || t('purchases.supplierHasPurchases'), "warning");
                  }
                }}
                style={{
                  flex: 1, height: 38, borderRadius: 8,
                  background: tk.danger, border: 'none',
                  color: '#fff', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{filterSelect.css}{formSelect.css}</style>
    </div>
  );
};

export default Purchases;
